import React, { useState, useEffect } from 'react';
import { API_BASE } from '../config';
import LoginPage from './LoginPage';

export default function AdminPanel() {
  const [token, setToken] = useState(localStorage.getItem('adminToken') || null);
  const [activeTab, setActiveTab] = useState('upload');
  const [alert, setAlert] = useState({ type: '', message: '' });

  const [uploadData, setUploadData] = useState({
    category: '', title: '', price: '', badge: '', info: '', description: '', status: 'unsold'
  });
  const [uploadPhoto, setUploadPhoto] = useState(null);
  const [uploadLoading, setUploadLoading] = useState(false);

  const [valuations, setValuations] = useState([]);
  const [valuationsLoading, setValuationsLoading] = useState(false);

  const [bikes, setBikes] = useState([]);
  const [bikesLoading, setBikesLoading] = useState(false);
  const [filterCategory, setFilterCategory] = useState('all');
  const [statusLoading, setStatusLoading] = useState(null);

  const [editBike, setEditBike] = useState(null);
  const [editPhoto, setEditPhoto] = useState(null);
  const [editLoading, setEditLoading] = useState(false);

  useEffect(() => {
    if (!token) return;
    if (activeTab === 'valuations') loadValuations();
    else if (activeTab === 'manageBikes') loadManageBikes();
  }, [activeTab, token]);

  const showAlert = (type, message) => {
    setAlert({ type, message });
    setTimeout(() => setAlert({ type: '', message: '' }), 4000);
  };

  const handleLogout = () => {
    localStorage.removeItem('adminToken');
    setToken(null);
    showAlert('success', 'Logged out successfully');
  };

  const handleUploadChange = (e) => {
    const { name, value } = e.target;
    setUploadData(prev => ({ ...prev, [name]: value }));
  };

  const handleUploadSubmit = async (e) => {
    e.preventDefault();
    if (!uploadPhoto) { showAlert('danger', 'Please select a photo'); return; }
    setUploadLoading(true);
    setAlert({ type: '', message: '' });
    try {
      const formData = new FormData();
      Object.entries(uploadData).forEach(([k, v]) => formData.append(k, v));
      formData.append('photo', uploadPhoto);
      const response = await fetch(`${API_BASE}/app/admin/bikes`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: formData
      });
      const result = await response.json();
      if (response.ok) {
        showAlert('success', 'Bike successfully uploaded!');
        setUploadData({ category: '', title: '', price: '', badge: '', info: '', description: '', status: 'unsold' });
        setUploadPhoto(null);
        e.target.reset();
      } else if (response.status === 401) {
        handleLogout();
        throw new Error('Session expired. Please login again.');
      } else {
        throw new Error(result.error || 'Failed to upload');
      }
    } catch (err) {
      showAlert('danger', err.message);
    } finally {
      setUploadLoading(false);
    }
  };

  const loadValuations = async () => {
    setValuationsLoading(true);
    try {
      const response = await fetch(`${API_BASE}/app/admin/valuations`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      if (!response.ok) {
        if (response.status === 401) { handleLogout(); throw new Error('Session expired'); }
        throw new Error(data.detail || 'Failed to load valuations');
      }
      setValuations(data);
    } catch (err) {
      showAlert('danger', err.message);
    } finally {
      setValuationsLoading(false);
    }
  };

  const deleteValuation = async (id) => {
    if (!window.confirm('Are you sure you want to delete this valuation request?')) return;
    try {
      const response = await fetch(`${API_BASE}/app/admin/valuations/${id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) { showAlert('success', 'Valuation request deleted.'); loadValuations(); }
      else { const result = await response.json(); throw new Error(result.detail || 'Failed to delete request'); }
    } catch (err) { showAlert('danger', err.message); }
  };

  const loadManageBikes = async () => {
    setBikesLoading(true);
    try {
      const response = await fetch(`${API_BASE}/app/bikes`);
      const data = await response.json();
      if (!response.ok) throw new Error(data.detail || 'Failed to load bikes');
      setBikes(data);
    } catch (err) {
      showAlert('danger', err.message);
    } finally {
      setBikesLoading(false);
    }
  };

  const openEdit = (bike) => { setEditBike({ ...bike }); setEditPhoto(null); };

  const handleEditChange = (e) => {
    const { name, value } = e.target;
    setEditBike(prev => ({ ...prev, [name]: value }));
  };

  const handleEditSubmit = async (e) => {
    e.preventDefault();
    setEditLoading(true);
    try {
      const formData = new FormData();
      ['category', 'title', 'price', 'badge', 'info', 'description', 'status'].forEach(k =>
        formData.append(k, editBike[k] || '')
      );
      if (editPhoto) formData.append('photo', editPhoto);
      const response = await fetch(`${API_BASE}/app/admin/bikes/${editBike.id}`, {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${token}` },
        body: formData
      });
      const result = await response.json();
      if (!response.ok) throw new Error(result.error || 'Failed to update');
      showAlert('success', 'Bike updated successfully!');
      setEditBike(null);
      loadManageBikes();
    } catch (err) {
      showAlert('danger', err.message);
    } finally {
      setEditLoading(false);
    }
  };

  const toggleStatus = async (bike) => {
    const newStatus = bike.status === 'sold' ? 'unsold' : 'sold';
    setStatusLoading(bike.id);
    try {
      const formData = new FormData();
      ['category', 'title', 'price', 'badge', 'info', 'description'].forEach(k =>
        formData.append(k, bike[k] || '')
      );
      formData.append('status', newStatus);
      const response = await fetch(`${API_BASE}/app/admin/bikes/${bike.id}`, {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${token}` },
        body: formData
      });
      if (!response.ok) throw new Error('Failed to update status');
      setBikes(prev => prev.map(b => b.id === bike.id ? { ...b, status: newStatus } : b));
    } catch (err) {
      showAlert('danger', err.message);
    } finally {
      setStatusLoading(null);
    }
  };

  const deleteBike = async (id) => {
    if (!window.confirm('Are you sure you want to delete this bike advertisement?')) return;
    try {
      const response = await fetch(`${API_BASE}/app/admin/bikes/${id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) { showAlert('success', 'Bike deleted successfully!'); loadManageBikes(); }
      else {
        const result = await response.json();
        if (response.status === 401) { handleLogout(); throw new Error('Session expired'); }
        throw new Error(result.error || 'Failed to delete bike');
      }
    } catch (err) { showAlert('danger', err.message); }
  };

  if (!token) return <LoginPage onLogin={setToken} />;

  return (
    <div className="container" style={{ marginTop: '60px', marginBottom: '100px' }}>
      <div className="row justify-content-center">
        <div className="col-md-10">
          <div className="d-flex justify-content-between align-items-center mb-4">
            <div className="d-flex align-items-center gap-3">
              <img src="/shivam motor logo.jpeg" alt="Logo" style={{ height: '60px', borderRadius: '50%' }} />
              <h2 className="mb-0 fw-bold fs-3 d-none d-sm-block">Shivam Moto - Admin Panel</h2>
              <h2 className="mb-0 fw-bold fs-4 d-block d-sm-none">Admin Panel</h2>
            </div>
            <button onClick={handleLogout} className="btn btn-outline-danger btn-sm px-3 rounded-pill">
              Logout
            </button>
          </div>

          {alert.message && (
            <div className={`alert alert-${alert.type}`} role="alert">{alert.message}</div>
          )}

          <ul className="nav nav-pills nav-fill mb-4" id="adminTabs">
            {[['upload', 'Upload Bike'], ['valuations', 'Valuation Requests'], ['manageBikes', 'Manage Bikes']].map(([tab, label]) => (
              <li className="nav-item" key={tab}>
                <button
                  className={`nav-link fw-bold ${activeTab === tab ? 'active bg-warning text-dark' : 'text-secondary'}`}
                  onClick={() => setActiveTab(tab)}
                >
                  {label}
                </button>
              </li>
            ))}
          </ul>

          <div className="tab-content">
            {activeTab === 'upload' && (
              <div className="admin-card">
                <h4 className="mb-4 fw-bold">Upload New Bike / Offer</h4>
                <form onSubmit={handleUploadSubmit}>
                  <div className="mb-3">
                    <label className="form-label">Category *</label>
                    <select className="form-select" name="category" value={uploadData.category} onChange={handleUploadChange} required>
                      <option value="">Select Category...</option>
                      <option value="new">New Bike</option>
                      <option value="old">Old Bike</option>
                      <option value="offer">Today's Offer</option>
                    </select>
                  </div>
                  <div className="row">
                    <div className="col-md-6 mb-3">
                      <label className="form-label">Bike Title *</label>
                      <input type="text" className="form-control" name="title" placeholder="e.g. Hero Splendor Plus" value={uploadData.title} onChange={handleUploadChange} required />
                    </div>
                    <div className="col-md-6 mb-3">
                      <label className="form-label">Price *</label>
                      <input type="text" className="form-control" name="price" placeholder="e.g. ₹1,00,500" value={uploadData.price} onChange={handleUploadChange} required />
                    </div>
                  </div>
                  <div className="row">
                    <div className="col-md-6 mb-3">
                      <label className="form-label">Badge (Optional)</label>
                      <input type="text" className="form-control" name="badge" placeholder="e.g. New 2026, Top Seller" value={uploadData.badge} onChange={handleUploadChange} />
                    </div>
                    <div className="col-md-6 mb-3">
                      <label className="form-label">Additional Info (Optional)</label>
                      <input type="text" className="form-control" name="info" placeholder="e.g. 2026 | Petrol | 70 km/l" value={uploadData.info} onChange={handleUploadChange} />
                    </div>
                  </div>
                  <div className="mb-3">
                    <label className="form-label">Description (Optional)</label>
                    <textarea className="form-control" name="description" rows="3" placeholder="Enter detailed bike description..." value={uploadData.description} onChange={handleUploadChange}></textarea>
                  </div>
                  <div className="row">
                    <div className="col-md-6 mb-3">
                      <label className="form-label">Status *</label>
                      <select className="form-select" name="status" value={uploadData.status} onChange={handleUploadChange} required>
                        <option value="unsold">Unsold (Available)</option>
                        <option value="sold">Sold Out</option>
                      </select>
                    </div>
                    <div className="col-md-6 mb-4">
                      <label className="form-label">Photo *</label>
                      <input className="form-control" type="file" name="photo" accept="image/*" onChange={e => e.target.files && setUploadPhoto(e.target.files[0])} required />
                    </div>
                  </div>
                  <button type="submit" className="btn btn-dark w-100 py-2 mt-2 fw-bold" disabled={uploadLoading}>
                    {uploadLoading ? 'Uploading...' : 'Upload Bike'}
                  </button>
                </form>
              </div>
            )}

            {activeTab === 'valuations' && (
              <div className="admin-card">
                <div className="d-flex justify-content-between align-items-center mb-4">
                  <h4 className="mb-0 fw-bold">Valuation Requests</h4>
                  <button className="btn btn-sm btn-outline-primary px-3 rounded-pill" onClick={loadValuations} disabled={valuationsLoading}>
                    <i className="fas fa-sync-alt me-1"></i> Refresh
                  </button>
                </div>
                {valuationsLoading ? (
                  <div className="text-center py-5"><div className="spinner-border spinner-border-sm text-muted me-2"></div>Loading requests...</div>
                ) : valuations.length === 0 ? (
                  <div className="text-center py-5 text-muted">No valuation requests found.</div>
                ) : (
                  <div className="table-responsive">
                    <table className="table table-hover">
                      <thead className="table-light">
                        <tr><th>Date</th><th>Customer</th><th>Bike Details</th><th>Location</th><th>Actions</th></tr>
                      </thead>
                      <tbody>
                        {valuations.map(v => (
                          <tr key={v.id}>
                            <td>
                              <small className="text-muted d-block">{new Date(v.created_at).toLocaleDateString()}</small>
                              <small className="text-muted">{new Date(v.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</small>
                            </td>
                            <td>
                              <div className="fw-bold">{v.name}</div>
                              <a href={`tel:${v.phone}`} className="text-decoration-none small"><i className="fas fa-phone-alt me-1"></i>{v.phone}</a>
                            </td>
                            <td>
                              <div className="fw-semibold">{v.bike_model}</div>
                              <small className="text-muted">{v.year || 'N/A'} | {v.kilometers || 'N/A'} km</small>
                            </td>
                            <td><span className="badge bg-light text-dark border">{v.city || 'N/A'}</span></td>
                            <td>
                              <button className="btn btn-sm btn-outline-danger" onClick={() => deleteValuation(v.id)}>
                                <i className="fas fa-trash-alt"></i>
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            )}

            {activeTab === 'manageBikes' && (
              <div className="admin-card">
                <div className="d-flex justify-content-between align-items-center mb-3">
                  <h4 className="mb-0 fw-bold">Manage Bikes</h4>
                  <button className="btn btn-sm btn-outline-primary px-3 rounded-pill" onClick={loadManageBikes} disabled={bikesLoading}>
                    <i className="fas fa-sync-alt me-1"></i> Refresh
                  </button>
                </div>
                <div className="d-flex gap-2 flex-wrap mb-3">
                  {['all', 'new', 'old', 'offer'].map(cat => (
                    <button
                      key={cat}
                      className={`btn btn-sm rounded-pill px-3 ${filterCategory === cat ? 'btn-dark' : 'btn-outline-secondary'}`}
                      onClick={() => setFilterCategory(cat)}
                    >
                      {cat === 'all' ? 'All' : cat === 'new' ? 'New Bike' : cat === 'old' ? 'Old Bike' : "Today's Offer"}
                    </button>
                  ))}
                </div>
                {bikesLoading ? (
                  <div className="text-center py-5"><div className="spinner-border spinner-border-sm text-muted me-2"></div>Loading bikes...</div>
                ) : bikes.length === 0 ? (
                  <div className="text-center py-5 text-muted">No bikes found.</div>
                ) : (
                  <div className="table-responsive">
                    <table className="table table-hover">
                      <thead className="table-light">
                        <tr><th>ID</th><th>Title</th><th>Category</th><th>Price</th><th>Status</th><th>Actions</th></tr>
                      </thead>
                      <tbody>
                        {bikes.filter(b => filterCategory === 'all' || b.category === filterCategory).map(b => (
                          <React.Fragment key={b.id}>
                            <tr>
                              <td>{b.id}</td>
                              <td>
                                <div className="fw-bold">{b.title}</div>
                                <small className="text-muted">{b.info || ''}</small>
                              </td>
                              <td><span className="badge bg-light text-dark border">{b.category?.toUpperCase()}</span></td>
                              <td>{b.price}</td>
                              <td>
                                <button
                                  className={`btn btn-sm rounded-pill px-3 me-1 ${b.status === 'sold' ? 'btn-danger' : 'btn-success'}`}
                                  onClick={() => toggleStatus(b)}
                                  disabled={statusLoading === b.id}
                                  title="Click to toggle status"
                                >
                                  {statusLoading === b.id
                                    ? <span className="spinner-border spinner-border-sm"></span>
                                    : b.status === 'sold' ? 'Sold' : 'Available'
                                  }
                                </button>
                              </td>
                              <td className="d-flex gap-2">
                                <button className="btn btn-sm btn-outline-warning px-3 rounded-pill" onClick={() => openEdit(b)}>
                                  <i className="fas fa-edit me-1"></i> Edit
                                </button>
                                <button className="btn btn-sm btn-outline-danger px-3 rounded-pill" onClick={() => deleteBike(b.id)}>
                                  <i className="fas fa-trash-alt me-1"></i> Delete
                                </button>
                              </td>
                            </tr>
                            {editBike?.id === b.id && (
                              <tr>
                                <td colSpan="6" className="bg-light">
                                  <form onSubmit={handleEditSubmit} className="p-2">
                                    <div className="row g-2">
                                      <div className="col-md-4">
                                        <label className="form-label small">Category *</label>
                                        <select className="form-select form-select-sm" name="category" value={editBike.category} onChange={handleEditChange} required>
                                          <option value="new">New Bike</option>
                                          <option value="old">Old Bike</option>
                                          <option value="offer">Today's Offer</option>
                                        </select>
                                      </div>
                                      <div className="col-md-4">
                                        <label className="form-label small">Title *</label>
                                        <input className="form-control form-control-sm" name="title" value={editBike.title} onChange={handleEditChange} required />
                                      </div>
                                      <div className="col-md-4">
                                        <label className="form-label small">Price *</label>
                                        <input className="form-control form-control-sm" name="price" value={editBike.price} onChange={handleEditChange} required />
                                      </div>
                                      <div className="col-md-4">
                                        <label className="form-label small">Badge</label>
                                        <input className="form-control form-control-sm" name="badge" value={editBike.badge || ''} onChange={handleEditChange} />
                                      </div>
                                      <div className="col-md-4">
                                        <label className="form-label small">Additional Info</label>
                                        <input className="form-control form-control-sm" name="info" value={editBike.info || ''} onChange={handleEditChange} />
                                      </div>
                                      <div className="col-md-4">
                                        <label className="form-label small">Status *</label>
                                        <select className="form-select form-select-sm" name="status" value={editBike.status} onChange={handleEditChange} required>
                                          <option value="unsold">Unsold (Available)</option>
                                          <option value="sold">Sold Out</option>
                                        </select>
                                      </div>
                                      <div className="col-12">
                                        <label className="form-label small">Description</label>
                                        <textarea className="form-control form-control-sm" name="description" rows="2" value={editBike.description || ''} onChange={handleEditChange} />
                                      </div>
                                      <div className="col-md-6">
                                        <label className="form-label small">Replace Photo (optional)</label>
                                        <input className="form-control form-control-sm" type="file" accept="image/*" onChange={e => setEditPhoto(e.target.files[0] || null)} />
                                        {editBike.image_url && !editPhoto && (
                                          <small className="text-muted">Current: {editBike.image_url}</small>
                                        )}
                                      </div>
                                    </div>
                                    <div className="d-flex gap-2 mt-3">
                                      <button type="submit" className="btn btn-sm btn-dark px-4 rounded-pill" disabled={editLoading}>
                                        {editLoading ? 'Saving...' : 'Save Changes'}
                                      </button>
                                      <button type="button" className="btn btn-sm btn-outline-secondary px-4 rounded-pill" onClick={() => setEditBike(null)}>
                                        Cancel
                                      </button>
                                    </div>
                                  </form>
                                </td>
                              </tr>
                            )}
                          </React.Fragment>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
